import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { TravelEditDetailsMenubar } from "components/layout/MenuBar";
import Nav from "components/layout/Nav";
import { fetchLocations, setSelectedLocation, updateLocation } from "../../../../Slices/travel.slice";
import { Overview } from "components/forms/overview";
import { ThumbnailPicker } from "components/forms/thumbnailPicker";
import { HowToReachTravel } from "components/forms/howToReach";
import { ImageGallery } from "components/forms/commonForms";
import { showToast } from "components/shared/Toast";
import { buildEditDetailsPath, matchesLocationParam } from "utils/locationSlug";

// ✅ Dynamic Form Imports
import { MajorAttractions, MustExperince, BestPlaces, Festival, MustBuy, StapleFood, FamousFood } from "components/forms/commonForms";

const TravelEditdetails = () => {
    const { slug, form } = useParams();
    const [selectedForm, setSelectedForm] = useState(form || "Overview");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedLocation, locations, loading, error } = useSelector((state) => state.travel);
    const { user } = useSelector((state) => state.auth);
    const [isApproving, setIsApproving] = useState(false);

    // ✅ Map form names to components
    const formComponents = {
        Overview: Overview,
        ThumbnailImages: ThumbnailPicker,
        MajorAttractions: MajorAttractions,
        BestPlaces: BestPlaces,
        MustExperince: MustExperince,
        Festival: Festival,
        MustBuy: MustBuy,
        FamousFood: FamousFood,
        StapleFood: StapleFood,
        HowtoReach: HowToReachTravel,
        imageGallery: ImageGallery,
    };

    // ✅ Fetch locations on mount if not available
    useEffect(() => {
        const loadData = async () => {
            if (locations.length === 0) {
                await dispatch(fetchLocations());
            }
        };
        loadData();
    }, [dispatch, locations.length]);

    // ✅ Select location after locations are fetched
    useEffect(() => {
        if (locations.length && slug) {
            const location = locations.find((loc) => matchesLocationParam(loc, slug));
            dispatch(setSelectedLocation(location || null));
            if (!location) {
                console.warn("Location not found for route:", slug);
            }
        }
    }, [dispatch, slug, locations]);

    // ✅ Update form selection from URL
    useEffect(() => {
        if (form) {
            setSelectedForm(form);
        }
    }, [form]);

    // ✅ Handle form selection with URL update
    const handleFormSelection = (formName) => {
        setSelectedForm(formName);
        if (selectedLocation) {
            navigate(buildEditDetailsPath("travel", selectedLocation, formName), { replace: true });
        }
    };

    // ✅ Handle form submission with real-time updates
    const handleFormSubmit = async (formData) => {
        if (selectedLocation) {
            try {
                const response = await dispatch(
                    updateLocation({ locationId: selectedLocation._id, formData })
                ).unwrap();

                if (response) {
                    const nextLocation = { ...selectedLocation, ...response };
                    await dispatch(fetchLocations());
                    dispatch(setSelectedLocation(nextLocation));
                    navigate(buildEditDetailsPath("travel", nextLocation, selectedForm), { replace: true });
                }
            } catch (error) {
                console.error("❌ Error updating location:", error);
            }
        }
    };

    const handleApproveChanges = async () => {
        if (!selectedLocation || isApproving) return;
        try {
            setIsApproving(true);
            await dispatch(
                updateLocation({
                    locationId: selectedLocation._id,
                    formData: { reviewNote: "Approved by District Manager" },
                })
            ).unwrap();
            await dispatch(fetchLocations());
            showToast("Changes approved and sent to Super Admin.", "success");
        } catch (error) {
            console.error("❌ Error approving changes:", error);
            showToast("Failed to approve changes.", "error");
        } finally {
            setIsApproving(false);
        }
    };

    // ✅ Render dynamic form
    const renderForm = () => {
        const SelectedFormComponent = formComponents[selectedForm];
        return SelectedFormComponent ? (
            <SelectedFormComponent
                location={selectedLocation}
                onSubmit={handleFormSubmit}
                contentType="travel"
            />
        ) : <p>⚠️ Form not available</p>;
    };

    return (
        <>
            <Nav />
            <div className="row">
                <TravelEditDetailsMenubar
                    selectedForm={selectedForm}
                    setActiveComponent={handleFormSelection}
                    currentLocation={selectedLocation}
                />
                <main className="adminPageMain adminPageMain--slate">
                    {loading ? (
                        <div className="adminPageShell">
                            <h5>Loading location details...</h5>
                        </div>
                    ) : error ? (
                        <div className="adminPageShell">
                            <h5 style={{ color: "red" }}>Error: {error}</h5>
                        </div>
                    ) : selectedLocation ? (
                        <div className="adminPageShell">
                            <section className="editorPageShell">
                                {user?.role === "districtManager" && selectedLocation.approvalStatus === "pending_dm" && (
                                    <div className="editorPageActions">
                                        <button
                                            className="btn-blue"
                                            onClick={handleApproveChanges}
                                            disabled={isApproving}
                                            type="button"
                                        >
                                            {isApproving ? "Approving..." : "Approve Changes"}
                                        </button>
                                    </div>
                                )}
                                <div className="formWrapper">{renderForm()}</div>
                            </section>
                        </div>
                    ) : (
                        <div className="adminPageShell">
                            <h5>Location not found.</h5>
                        </div>
                    )}
                </main>
            </div>
        </>
    );
};

export default TravelEditdetails;
